/**
 * ProfileCompletionBanner - Displays a notification banner when advisor profile is incomplete
 *
 * Shows for users who registered before the role/RPK fields were added,
 * prompting them to complete their profile information.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X, ArrowRight, User, Shield } from "lucide-react";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import type { Advisor } from "@/shared/types";

interface ProfileCompletionBannerProps {
  className?: string;
}

/**
 * Check which profile fields are missing
 */
function getMissingFields(advisor: Advisor): string[] {
  const missing: string[] = [];

  // Role type is required
  if (!advisor.roleType) {
    missing.push("typ roli (doradca/ekspert)");
  }

  // Compensation model is required
  if (!advisor.compensationModel) {
    missing.push("model wynagrodzenia");
  }

  // For ekspert or oba roles, RPK data is required
  if (advisor.roleType === "ekspert" || advisor.roleType === "oba") {
    if (!advisor.rpkNumber) {
      missing.push("numer wpisu RPK");
    }
    if (!advisor.rpkDivision) {
      missing.push("dział RPK");
    }
  }

  return missing;
}

/**
 * Check if the advisor profile is complete
 */
function isProfileComplete(advisor: Advisor): boolean {
  return getMissingFields(advisor).length === 0;
}

export function ProfileCompletionBanner({ className = "" }: ProfileCompletionBannerProps) {
  const { advisor } = useAdvisorStore();
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if no advisor, profile is complete, or dismissed
  if (!advisor || isProfileComplete(advisor) || isDismissed) {
    return null;
  }

  const missingFields = getMissingFields(advisor);
  const isRpkPending = advisor.rpkNumber && !advisor.rpkVerified;

  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-amber-800">
            Uzupelnij swoj profil
          </h3>

          <div className="mt-2 text-sm text-amber-700">
            <p>
              Aby w pelni korzystac z panelu doradcy, prosimy o uzupelnienie
              brakujacych informacji:
            </p>

            <ul className="mt-2 space-y-1">
              {missingFields.map((field) => (
                <li key={field} className="flex items-center gap-2">
                  <User className="w-3 h-3 flex-shrink-0" />
                  <span>{field}</span>
                </li>
              ))}
            </ul>

            {isRpkPending && (
              <div className="mt-3 flex items-center gap-2 text-amber-600">
                <Shield className="w-4 h-4" />
                <span className="text-xs">
                  Twoj wpis RPK oczekuje na weryfikacje przez administratora
                </span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
            >
              Uzupelnij profil
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className="flex-shrink-0 p-1 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors"
          aria-label="Zamknij powiadomienie"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Hook to check if the current advisor has an incomplete profile
 */
export function useProfileCompletion() {
  const { advisor } = useAdvisorStore();

  if (!advisor) {
    return {
      isComplete: true,
      missingFields: [],
      isRpkPending: false,
    };
  }

  const missingFields = getMissingFields(advisor);

  return {
    isComplete: missingFields.length === 0,
    missingFields,
    isRpkPending: !!(advisor.rpkNumber && !advisor.rpkVerified),
  };
}
